/**
 * video-pause.js
 * Adds an accessible Pause/Play control to auto-playing / looping videos so they
 * can be stopped by keyboard and screen-reader users (WCAG 2.1 SC 2.2.2 — Pause,
 * Stop, Hide). Also pauses auto-playing video by default when the user has
 * requested reduced motion. No dependencies.
 */
(function () {
  'use strict';

  var PAUSE_LABEL = 'Pause background video';
  var PLAY_LABEL = 'Play background video';
  var prefersReduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function injectStyles() {
    if (document.getElementById('a11y-video-toggle-styles')) return;
    var style = document.createElement('style');
    style.id = 'a11y-video-toggle-styles';
    style.textContent =
      '.a11y-video-toggle{position:absolute;bottom:12px;right:12px;z-index:6;' +
      'width:40px;height:40px;border-radius:50%;border:2px solid #fff;' +
      'background:rgba(0,0,0,.55);cursor:pointer;display:flex;align-items:center;' +
      'justify-content:center;padding:0;margin:0;box-sizing:border-box;' +
      'transition:background .15s ease}' +
      '.a11y-video-toggle:hover{background:rgba(0,0,0,.85)}' +
      '.a11y-video-toggle:focus-visible{outline:3px solid #118849;outline-offset:2px}' +
      '.a11y-video-toggle::before{content:"";display:block;box-sizing:border-box}' +
      '.a11y-video-toggle[data-state="playing"]::before{width:12px;height:14px;' +
      'border-left:4px solid #fff;border-right:4px solid #fff}' +
      '.a11y-video-toggle[data-state="paused"]::before{width:0;height:0;margin-left:3px;' +
      'border-top:7px solid transparent;border-bottom:7px solid transparent;' +
      'border-left:12px solid #fff}';
    document.head.appendChild(style);
  }

  // A video already has its own pause/play affordance if it exposes native
  // controls, is managed by a player (Plyr / video.js), or the section ships a
  // dedicated toggle (this theme's data-video-toggle / data-play-button, or a
  // Plyr overlaid play control). In those cases we must NOT add a second button.
  var CONTROL_SELECTORS =
    '[data-video-toggle],[data-play-button],' +
    '.plyr__control--overlaid,.plyr__control[data-plyr="play"]';

  function hasOwnControl(video) {
    if (video.controls) return true;
    if (video.classList.contains('video-js')) return true;
    if (video.closest('.plyr, .video-js')) return true;
    var scope =
      video.closest(
        '.shopify-section, section, [data-video-full], .video-wrapper, .featured-video'
      ) || document;
    return !!scope.querySelector(CONTROL_SELECTORS);
  }

  function addToggle(video) {
    if (video.getAttribute('data-a11y-pause-ready') === 'true') return;
    // Only decorate videos that auto-play or loop AND have no control of their own.
    var autoplays =
      video.autoplay || video.hasAttribute('autoplay') || video.loop;
    if (!autoplays || hasOwnControl(video)) return;

    video.setAttribute('data-a11y-pause-ready', 'true');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'a11y-video-toggle';

    function setState(isPlaying) {
      btn.setAttribute('aria-label', isPlaying ? PAUSE_LABEL : PLAY_LABEL);
      btn.setAttribute('data-state', isPlaying ? 'playing' : 'paused');
    }

    btn.addEventListener('click', function () {
      if (video.paused) {
        var p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () {});
        }
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', function () { setState(true); });
    video.addEventListener('pause', function () { setState(false); });

    var parent = video.parentElement;
    if (parent) {
      var position = window.getComputedStyle(parent).position;
      if (position === 'static') parent.style.position = 'relative';
      parent.appendChild(btn);
    } else {
      video.insertAdjacentElement('afterend', btn);
    }

    if (prefersReduced) {
      video.removeAttribute('autoplay');
      try { video.pause(); } catch (e) {}
      setState(false);
    } else {
      setState(!video.paused);
    }
  }

  function scan() {
    var videos = document.getElementsByTagName('video');
    for (var i = 0; i < videos.length; i++) {
      addToggle(videos[i]);
    }
  }

  function init() {
    injectStyles();
    scan();

    // Videos can be injected lazily (sliders, Replo, app embeds) — watch for them.
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scan();
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
